import {useCallback, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {
	ChatRoomsEnum,
	FeaturesEnum,
	HomeEnum,
	MyPaymentsEnum,
	MyReferralsEnum,
	OperationStateTypesEnum,
} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {useAccountInformation, useCallChatRoom} from 'presentation/pages/private/hooks';
import {useContractStatus, usePersonalData} from 'presentation/hooks';
import {useElectronicBillSubscription} from 'presentation/pages/private/hooks/useElectronicBillSubscription';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {useGetOpenObligationListQuery} from 'presentation/pages/private/my-payments-flow/tab-pages/open-bills/queries/useGetOpenObligationListQuery';
import {useGetInvoicesQuery} from 'presentation/pages/private/my-payments-flow/tab-pages/open-bills/queries/useGetInvoicesQuery';
import {useNewBillsBemobiFeatureFlag} from 'presentation/pages/private/hooks/useNewBillsBemobiFeatureFlag';
import {useSubscriptionDialogsStateStore} from 'presentation/components/SubscriptionDialogs/store/useSubscriptionDialogsStateStore';
import {useGetHistoryYearsQuery} from 'presentation/pages/private/my-payments-flow/queries/useGetHistoryYearsQuery';
import {NotificationCardProps} from '../components/NotificationCard/types';
import {NotificationId} from '../constants';
import {useNotificationsStore} from '../store/notificationStore';
import {useRegistrationUpdateStatusStore} from 'presentation/pages/private/store/useRegistrationUpdateStatusStore';

const {markAsRead} = translations['pt-br'].commons;

const {
	notificationList: {
		irDemonstrative,
		contractSuspended,
		paymentPending,
		referrals,
		digitalBill,
		registrationData,
	},
} = translations['pt-br'].privateFlow.notifications;

export function useNotifications() {
	const location = useLocation();
	const navigate = useNavigate();

	const [subscriptionOperationState, setOperationSubscriptionState] =
		useState<OperationStateTypesEnum>(OperationStateTypesEnum.NONE);
	const isSubmittingSubscription =
		subscriptionOperationState === OperationStateTypesEnum.SUBMITTING;

	const setDialogs = useSubscriptionDialogsStateStore(state => state.updateState);
	const {markAsRead: markNotificationAsRead, isRead: isNotificationRead} =
		useNotificationsStore();

	const {needsUpdate: registrationNeedsUpdate} = useRegistrationUpdateStatusStore();

	const {currentEmail} = usePersonalData();
	const {callChatRoom} = useCallChatRoom();
	const [showRegistrationDataDrawer, setShowRegistrationDataDrawer] = useState(false);

	const {data: appointmentsData, isLoading} = useGetHomeQuery();
	const {isEnabled: isNewBillsEnabled} = useNewBillsBemobiFeatureFlag();
	const {data: openObligationData} = useGetOpenObligationListQuery(!isNewBillsEnabled);
	const {data: invoicesData} = useGetInvoicesQuery(isNewBillsEnabled);

	const {data: availableYears = [], isLoading: isLoadingYears} = useGetHistoryYearsQuery({
		isShowCurrentYear: false,
	});

	const {
		isHolder,
		isResponsibleWithoutBenefits,
		healthOnly,
		healthOdonto,
		flPad,
		accesses,
		isB2BAccess,
		businessPlan,
		flAdministradora,
	} = useAccountInformation();

	const {
		handleSubscriptionResponse,
		handleCloseSubscriptionSucessModal,
		handleToggleShowInfoModal,
		handleToggleSubscriptionModal,
		protocol,
		isSubscribed,
		hasFinancialAcess,
	} = useElectronicBillSubscription();

	const {isContractSuspended, isValid} = useContractStatus();

	const {isEnabled: isMyReferralsFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_MEUS_ENCAMINHAMENTOS,
	);

	const hasExpiredObligation = isNewBillsEnabled
		? invoicesData?.obrigacoesPendentes?.some(bill => bill?.qtdDiasAtraso >= 5)
		: openObligationData?.some(
				bill =>
					bill &&
					bill?.linhaDigitavel &&
					bill?.linhaDigitavel?.qtDiasAtraso &&
					bill?.linhaDigitavel?.qtDiasAtraso >= 5,
			);
	const myReferrals = appointmentsData?.myReferrals;
	const needSubscription = isHolder && !isSubscribed && hasFinancialAcess;
	const showMyReferrals =
		isValid &&
		!isContractSuspended &&
		!isResponsibleWithoutBenefits &&
		isMyReferralsFeatureFlagEnabled &&
		(healthOnly || healthOdonto) &&
		myReferrals;
	const shouldShowTab = flPad || (!businessPlan && !flAdministradora);
	const showIRBase = shouldShowTab || (businessPlan && accesses.incomeTaxEmploy);

	const showIrDemonstrative = () => {
		const currentYear = new Date().getFullYear();
		const lastYear = Number(availableYears[0]);

		if (!showIRBase || isB2BAccess || lastYear !== currentYear - 1) {
			return false;
		}

		return isValid || isResponsibleWithoutBenefits;
	};

	const handleOpenSubscriptionModal = () => {
		setDialogs({
			isSubscriptionModalOpen: true,
		});
	};

	const handleBack = useCallback(() => {
		if (location.state?.pathname && location.state?.search) {
			navigate(`${location.state.pathname}${location.state.search}`);
			return;
		}

		if (location.state?.includes('/servicos')) {
			navigate(location.state);
			return;
		}

		navigate(HomeEnum.HOME_PAGE);
	}, [location]);

	const handleCloseRegistrationDataDrawer = () => {
		setShowRegistrationDataDrawer(false);
	};

	const notificationsList: NotificationCardProps[] = [
		{
			id: NotificationId.CONTRACT_SUSPENDED,
			isActionRequired: true,
			shouldShow: isContractSuspended,
			title: contractSuspended.title,
			description: contractSuspended.description,
			iconName: 'warning-outlined',
			variantIcon: 'warning',
			unread: !isNotificationRead(NotificationId.CONTRACT_SUSPENDED),
			actions: [
				{
					label: contractSuspended.textButton,
					variant: 'outlined',
					onClick: () => callChatRoom(ChatRoomsEnum.DEBIT_NEGOTIATION),
				},
			],
		},
		{
			id: NotificationId.PAYMENT_PENDING,
			isActionRequired: true,
			shouldShow: hasExpiredObligation,
			title: paymentPending.title,
			description: paymentPending.description,
			iconName: 'warning-outlined',
			variantIcon: 'warning',
			unread: !isNotificationRead(NotificationId.PAYMENT_PENDING),
			actions: [
				{
					label: paymentPending.textButton,
					variant: 'outlined',
					onClick: () => navigate(MyPaymentsEnum.OPEN_BILLS),
				},
			],
		},
		{
			id: NotificationId.IR_DEMONSTRATIVE,
			shouldShow: showIrDemonstrative(),
			title: irDemonstrative.title,
			description: irDemonstrative.description,
			iconName: 'lion-filled',
			variantIcon: 'primary',
			unread: !isNotificationRead(NotificationId.IR_DEMONSTRATIVE),
			actions: [
				{
					label: markAsRead,
					variant: 'text',
					shouldShow: !isNotificationRead(NotificationId.IR_DEMONSTRATIVE),
					onClick: () => {
						markNotificationAsRead(NotificationId.IR_DEMONSTRATIVE);
					},
				},
				{
					label: irDemonstrative.textButton,
					variant: 'outlined',
					onClick: () => {
						markNotificationAsRead(NotificationId.IR_DEMONSTRATIVE);
						navigate(MyPaymentsEnum.INCOME_TAX);
					},
				},
			],
		},
		{
			id: NotificationId.REFERRALS,
			shouldShow: showMyReferrals,
			title: referrals.title,
			description: referrals.description,
			iconName: 'assignment-outline',
			variantIcon: 'primary',
			unread: !isNotificationRead(NotificationId.REFERRALS),
			actions: [
				{
					label: markAsRead,
					variant: 'text',
					shouldShow: !isNotificationRead(NotificationId.REFERRALS),
					onClick: () => {
						markNotificationAsRead(NotificationId.REFERRALS);
					},
				},
				{
					label: referrals.textButton,
					variant: 'outlined',
					onClick: () => {
						markNotificationAsRead(NotificationId.REFERRALS);
						navigate(MyReferralsEnum.MY_REFERRALS);
					},
				},
			],
		},
		{
			id: NotificationId.DIGITAL_BILL,
			shouldShow: needSubscription,
			title: digitalBill.title,
			description: digitalBill.description,
			iconName: 'receipt-long-filled',
			variantIcon: 'primary',
			unread: !isNotificationRead(NotificationId.DIGITAL_BILL),
			actions: [
				{
					label: markAsRead,
					variant: 'text',
					shouldShow: !isNotificationRead(NotificationId.DIGITAL_BILL),
					onClick: () => {
						markNotificationAsRead(NotificationId.DIGITAL_BILL);
					},
				},
				{
					label: digitalBill.textButton,
					variant: 'outlined',
					onClick: handleOpenSubscriptionModal,
				},
			],
		},
		{
			id: NotificationId.REGISTRATION_DATA_UPDATE,
			shouldShow: registrationNeedsUpdate,
			title: registrationData.title,
			description: registrationData.description,
			iconName: 'warning-outlined',
			variantIcon: 'warning',
			unread: registrationNeedsUpdate,
			isActionRequired: registrationNeedsUpdate,
			actions: [
				{
					label: registrationData.unreadTextButton,
					variant: 'outlined',
					onClick: () => setShowRegistrationDataDrawer(true),
				},
			],
		},
	];

	const isNotificationsEmpty = notificationsList.every(notification => !notification.shouldShow);

	return {
		notificationsList,
		handleBack,
		isLoading,
		isLoadingYears,
		isSubmittingSubscription,
		currentEmail,
		protocol,
		handleSubscriptionResponse,
		handleCloseSubscriptionSucessModal,
		handleToggleShowInfoModal,
		handleToggleSubscriptionModal,
		setOperationSubscriptionState,
		isNotificationsEmpty,
		showRegistrationDataDrawer,
		handleCloseRegistrationDataDrawer,
	};
}
